import { z } from 'zod';

import { DynamicArtifactTemplate } from '@eluve/llm-outputs';
import {
  humanReadableDateAndTimeWithDashes,
  splitFullName,
} from '@eluve/utils';

import { AppointmentModel } from '../../models/appointment';
import { ChartType } from '../../models/chart';
import { PatientType } from '../../models/patient';
import {
  NormalizedChartResult,
  PatientEhrUrlArgs,
  VendorDataMapper,
  VendorSyncConfig,
} from '../vendor-data-mapper';

import { logo } from './logo';
import {
  fhirCompositionSchema,
  getSoapKeyFromSection,
  stripFhirHtml,
} from './types';

const orchardAppointmentSchema = z
  .object({
    id: z.string(),
    // Optional so records without a name still parse and get the
    // "New Session" fallback below instead of being dropped.
    name: z.string().optional(),
    patient: z
      .object({
        id: z.string(),
        fullName: z.string(),
      })
      .passthrough(),
  })
  .passthrough();

const orchardAppointmentsSchema = z.array(orchardAppointmentSchema);

export class OrchardVendorDataMapper implements VendorDataMapper {
  getAppointments(data: unknown): AppointmentModel[] {
    const parseResult = orchardAppointmentsSchema.safeParse(data);
    if (!parseResult.success) {
      return [];
    }
    const appointments = parseResult.data;
    return appointments.map((appointment) => {
      const fullName = splitFullName(appointment.patient.fullName);
      const startTime = new Date().toISOString();
      return {
        externalId: appointment.id,
        name:
          appointment.name ??
          `New Session ${humanReadableDateAndTimeWithDashes()}`,
        description: '',
        externalPatientId: appointment.patient.id,
        externalPractitionerId: undefined,
        firstName: fullName.firstName,
        lastName: fullName.lastName,
        startTime,
        endTime: '',
        rawEhrData: {},
        isArchived: false,
        isImportable: true,
        status: undefined,
        sessionType: undefined,
      };
    });
  }

  getVendorSchema = () => {
    const secretSchema = z.object({
      'Client Secret': z
        .string()
        .describe('The Client Secret provided by Orchard for API access.'),
    });
    const plainTextSchema = z.object({
      'Client ID': z
        .string()
        .describe('The Client ID provided by Orchard for API access.'),
      'Token Endpoint': z
        .string()
        .default('https://medplum.staging.orchard-ehr.com/oauth2/token'),
      'FHIR base URL': z
        .string()
        .default('https://medplum.staging.orchard-ehr.com/fhir/R4'),
    });
    return { secretSchema, plainTextSchema };
  };

  getPatient(_data: unknown): PatientType | null {
    return null;
  }

  getPatientEhrUrl({ domain }: PatientEhrUrlArgs): string {
    // Orchard patient routes require the org slug (/app/:orgSlug/patients/:id),
    // which isn't derivable from the domain alone. /app redirects to the
    // user's first organization.
    return `https://${domain}/app`;
  }

  getLogo(): string {
    return logo;
  }

  getHomeUrl(domain: string): string {
    return `https://${domain}`;
  }

  getChart(_data: unknown): ChartType {
    return {
      externalPatientId: '',
      externalChartId: '',
      externalAppointmentId: null,
      signedAt: null,
      rawData: {},
    };
  }

  convertChartToSummary(_args: {
    data: unknown;
    template: DynamicArtifactTemplate;
  }) {
    return null;
  }

  convertRawDataToJsonNote({
    rawData,
  }: {
    rawData: unknown;
    template: DynamicArtifactTemplate;
  }): NormalizedChartResult {
    const parsed = fhirCompositionSchema.parse(rawData);

    const chart: Record<string, string> = {};

    for (const section of parsed.section ?? []) {
      const soapKey = getSoapKeyFromSection(section);
      if (soapKey) {
        chart[soapKey] = stripFhirHtml(section.text?.div ?? '');
      }
    }

    const patientRef = parsed.subject?.reference ?? '';
    const encounterRef = parsed.encounter?.reference ?? '';

    return {
      chart,
      chartDate: parsed.date,
      externalAppointmentId: encounterRef.replace('Encounter/', ''),
      externalPatientId: patientRef.replace('Patient/', ''),
    };
  }

  getSyncConfig(): VendorSyncConfig {
    return {
      isSyncEnabled: true,
      canSignNoteInEhr: false,
      canSyncNoteToEhr: () => ({ canSync: true }),
      canSyncWithManualChartUrl: false,
      isImportEnabled: false,
      shouldDisplayConfirmationBeforeSync: false,
    };
  }
}
